"use client";

import ky, { type HTTPError, type Options } from "ky";
import { toast } from "sonner";
import { CookieType } from "@/constants";
import { BaseUrl } from "@/types/base-url";
import type { ResponseType } from "@/types/general";
import packageJson from "../../package.json";
import { refreshAccessToken } from "./fetch-refresh-token";
import { getCsrfToken } from "./helpers/csrf";
import { generateUUID, getUUID } from "./helpers/uuid";

const waitForInteraction = () =>
	new Promise((resolve) => {
		requestAnimationFrame(() => resolve(true));
	});

const createFetchInstance = (type: BaseUrl) => {
	const headers: HeadersInit = {
		accept: "application/json",
		"user-agent": `${packageJson.name}/${packageJson.version}`,
		"content-type": "application/json",
		"x-device-id": getUUID() || "",
		"x-app-platform": "cpa",
	};

	return ky.create({
		headers,
		prefixUrl: `/api/proxy/${type}`,
		timeout: 60000,
		hooks: {
			beforeRequest: [
				async (request) => {
					await waitForInteraction();

					if (!getUUID()) {
						generateUUID();
						request.headers.set("x-device-id", getUUID() || "");
					}

					// middleware.ts rejects mutating requests without the CSRF header.
					if (!["GET", "HEAD"].includes(request.method.toUpperCase())) {
						const csrf = await getCsrfToken();
						if (csrf) request.headers.set(CookieType.CSRF_TOKEN, csrf);
					}

					return request;
				},
			],
			afterResponse: [
				async (_request, _options, response, { retryCount }) => {
					if (response.status !== 401) return;

					// Only the first 401 of a call is treated as an expired token.
					// A 401 that survives a fresh token is an authorization problem
					// (wrong path, wrong role), and refreshing again only rotates the
					// refresh token until the backend rejects it and signs the user
					// out — one mis-addressed endpoint would end the whole session.
					if (retryCount > 0) return;

					let message: string | undefined;
					try {
						const body = (await response
							.clone()
							.json()) as ResponseType<unknown>;
						message = body?.message;
					} catch {
						// non-JSON body — fall through to refresh attempt
					}

					if (message === "Incorrect email or password") return;

					const newAccessToken = await refreshAccessToken();
					if (newAccessToken) {
						// ky's own forced retry, so the replay stays inside this call
						// and increments retryCount. Calling ky(request, options)
						// started a fresh call whose counter was always 0 — an
						// unbounded refresh loop.
						return ky.retry();
					}
					// On /auth there is nothing to expire and nowhere to send the
					// user; redirecting would remount the page and refetch forever.
					if (window.location.pathname.startsWith("/auth")) return;
					toast.error("Your session has expired. Please log in again.");
					return window.location.replace("/auth/login");
				},
			],
			beforeError: [
				async (error_) => {
					const error = error_ as HTTPError<unknown>;
					if (error.name === "HTTPError") {
						const errorJson: { message: string } = await error.response.json();
						if (errorJson.message !== "Unauthorized") {
							toast.error(errorJson?.message);
						}
					}
					return error_;
				},
			],
		},
	});
};

const clientFetch = async <R>(
	params: string | [string, Options?],
	type: BaseUrl = BaseUrl.DEFAULT,
) => {
	let url: string;
	let options: Options | undefined;

	if (typeof params === "string") {
		url = params;
		options = undefined;
	} else {
		[url, options] = params;
	}
	const fetchFn = createFetchInstance(type);

	return await fetchFn(url, {
		...(options || {}),
	}).json<R & { message: string }>();
};

export default clientFetch;
